import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import OTGToolbar from "../OTGToolBar";
import { ChevronDown } from 'lucide-react';

import { MAPBOX_ACCESS_TOKEN } from './mapboxConfig.js';

// Check if MapBox GL is available
let mapboxgl;
try {
    mapboxgl = require('mapbox-gl');
    // Import CSS
    require('mapbox-gl/dist/mapbox-gl.css');
} catch (error) {
    console.error('MapBox GL not available:', error);
}

// Set your MapBox access token here
if (mapboxgl) {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN
}

// Extended map styles
const MAP_STYLES = {
    outdoors: {
        url: 'mapbox://styles/mapbox/outdoors-v12',
        name: 'Outdoors',
        description: 'Perfect for hiking and outdoor activities'
    },
    streets: {
        url: 'mapbox://styles/mapbox/streets-v12',
        name: 'Streets',
        description: 'Standard street map'
    },
    satellite: {
        url: 'mapbox://styles/mapbox/satellite-v9',
        name: 'Satellite',
        description: 'Aerial imagery'
    },
    satelliteStreets: {
        url: 'mapbox://styles/mapbox/satellite-streets-v12',
        name: 'Satellite Streets',
        description: 'Satellite with street labels'
    },
    light: {
        url: 'mapbox://styles/mapbox/light-v11',
        name: 'Light',
        description: 'Clean light theme'
    },
    dark: {
        url: 'mapbox://styles/mapbox/dark-v11',
        name: 'Dark',
        description: 'Dark theme'
    },
    terrain: {
        url: 'mapbox://styles/mapbox/outdoors-v12',
        name: 'Terrain',
        description: 'Topographic style'
    }
};

const OTGEventsMap = () => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef([]); // Use ref instead of state for markers
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [containerReady, setContainerReady] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState('outdoors');
    const [styleDropdownOpen, setStyleDropdownOpen] = useState(false);
    const navigate = useNavigate();

    // Check if MapBox GL is available
    useEffect(() => {
        if (!mapboxgl) {
            setError('MapBox GL library not loaded. Please install: npm install mapbox-gl');
            setLoading(false);
        }
    }, []);

    // Track when container ref is attached
    useEffect(() => {
        if (mapContainer.current) {
            console.log('Container is now ready!');
            setContainerReady(true);
        }
    }, []);

    // Handle back button click
    const handleBackClick = () => {
        navigate(-1); // Go back to previous page
    };

    // Handle style change
    const handleStyleChange = (styleKey) => {
        setSelectedStyle(styleKey);
        setStyleDropdownOpen(false);

        if (map.current) {
            map.current.setStyle(MAP_STYLES[styleKey].url);

            // Re-add markers after style loads
            map.current.once('styledata', () => {
                console.log('Style changed to:', MAP_STYLES[styleKey].name);
            });
        }
    };

    // Fetch events from your API
    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/otgservices/getEventCoordinates.php');

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setEvents(data.data);
                setError(null);
                console.log('Successfully loaded', data.data.length, 'events');
            } else {
                throw new Error(data.error || 'Failed to fetch events');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching events:', err);
        } finally {
            setLoading(false);
        }
    };

    // Initialize map when container is ready
    useEffect(() => {
        if (map.current || !mapboxgl || !containerReady) return;

        console.log('Container ready, initializing map...');

        // Check if MapBox token is set
        if (!mapboxgl.accessToken || mapboxgl.accessToken === 'YOUR_MAPBOX_ACCESS_TOKEN') {
            setError('MapBox access token not configured. Please set your token.');
            setLoading(false);
            return;
        }

        try {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: MAP_STYLES[selectedStyle].url,
                center: [-122.4194, 37.7749],
                zoom: 8
            });

            console.log('Map instance created successfully');

            // Add navigation controls
            map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

            // Wait for map to load before fetching events
            map.current.on('load', () => {
                console.log('MapBox map loaded successfully');
                setMapLoaded(true);
                fetchEvents();
            });

            // Handle map load errors
            map.current.on('error', (e) => {
                console.error('MapBox load error:', e);
                setError(`Failed to load map: ${e.error?.message || 'Unknown error'}`);
                setLoading(false);
            });

        } catch (error) {
            console.error('Error initializing MapBox:', error);
            setError(`Map initialization failed: ${error.message}`);
            setLoading(false);
        }
    }, [containerReady, selectedStyle]);

    // Add markers when events data changes
    useEffect(() => {
        if (!map.current || !mapLoaded || !events.length) return;

        console.log('Adding markers for', events.length, 'events');

        // Clear existing markers using ref
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];

        const newMarkers = [];
        const bounds = new mapboxgl.LngLatBounds();
        const duplicateOffsets = new Map();

        events.forEach(event => {
            // Parse coordinates (format: "lat, lng")
            const coords = event.gpscoordinate.split(',').map(coord => parseFloat(coord.trim()));
            let [lat, lng] = coords;

            if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Invalid coordinates for event ${event.name}:`, event.gpscoordinate);
                return;
            }

            // Handle duplicate coordinates by adding small offset
            const coordKey = event.gpscoordinate;
            if (duplicateOffsets.has(coordKey)) {
                // Offset by small amount (0.001 degrees ≈ 100 meters)
                const offsetCount = duplicateOffsets.get(coordKey);
                const offset = offsetCount * 0.001;
                lat += offset;
                lng += offset;
                duplicateOffsets.set(coordKey, offsetCount + 1);
                console.log(`Offsetting duplicate location for event ${event.name} by ${offset} degrees`);
            } else {
                duplicateOffsets.set(coordKey, 1);
            }

            // Create popup content with navigation button
            const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <div style="padding: 10px; max-width: 250px;">
                  <h3 style="margin: 0 0 8px 0; color: #2563eb; font-size: 16px;">${event.name}</h3>
                  <p style="margin: 0 0 5px 0; color: #666; font-size: 14px;">
                    <strong>📍 Meet at:</strong><br>${event.meetaddress}
                  </p>
                  <p style="margin: 0 0 10px 0; color: #888; font-size: 12px;">
                    <strong>Summary:</strong> ${event.summary}
                  </p>
                  <button 
                    onclick="window.location.href='/events/view-event/${event.id}'" 
                    style="
                      width: 100%; 
                      padding: 8px 12px; 
                      background-color: #2563eb; 
                      color: white; 
                      border: none; 
                      border-radius: 4px; 
                      font-size: 14px; 
                      cursor: pointer;
                      transition: background-color 0.2s;
                    "
                    onmouseover="this.style.backgroundColor='#1d4ed8'"
                    onmouseout="this.style.backgroundColor='#2563eb'"
                  >
                    View Event Details
                  </button>
                </div>
            `);

            // Create marker
            const marker = new mapboxgl.Marker({
                color: '#2563eb'
            })
                .setLngLat([lng, lat])
                .setPopup(popup)
                .addTo(map.current);

            newMarkers.push(marker);
            bounds.extend([lng, lat]);
        });

        // Update markers ref
        markersRef.current = newMarkers;

        // Fit map to show all markers
        if (events.length > 0) {
            map.current.fitBounds(bounds, {
                padding: 50,
                maxZoom: 12
            });
        }
    }, [events, mapLoaded]); // Clean dependency array - no markers dependency needed

    // Cleanup markers on unmount
    useEffect(() => {
        return () => {
            // Cleanup markers when component unmounts
            markersRef.current.forEach(marker => marker.remove());
            markersRef.current = [];
        };
    }, []);

    return (
        <div className="w-screen min-h-screen bg-gray-50">
            <OTGToolbar/>
            <div className="w-full py-4 bg-white shadow-sm">
                <div className="flex justify-between items-center px-4">
                    <div></div>
                    {/* Empty left spacer */}
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center">Upcoming Events</h2>
                    <div className="flex gap-2 items-center">
                        <button
                            onClick={handleBackClick}
                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                            ← Back
                        </button>
                        <button
                            onClick={fetchEvents}
                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                            disabled={loading}
                        >
                            🔄 Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Map container - full viewport width */}
            <div className="relative w-screen">
                <div
                    ref={mapContainer}
                    className="w-full h-[calc(100vh-140px)] sm:h-[calc(100vh-120px)]"
                    style={{minHeight: '400px'}}
                />

                {/* Style selector dropdown - positioned over map */}
                <div className="absolute top-4 left-4 z-10">
                    <div className="relative">
                        <button
                            onClick={() => setStyleDropdownOpen(!styleDropdownOpen)}
                            className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-300 hover:shadow-xl transition-shadow text-sm font-medium"
                        >
                            <span>🗺️</span>
                            <span>{MAP_STYLES[selectedStyle].name}</span>
                            <ChevronDown
                                size={16}
                                className={`transition-transform ${styleDropdownOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {styleDropdownOpen && (
                            <>
                                {/* Backdrop */}
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setStyleDropdownOpen(false)}
                                />

                                {/* Dropdown menu */}
                                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-80 overflow-y-auto">
                                    {Object.entries(MAP_STYLES).map(([key, style]) => (
                                        <button
                                            key={key}
                                            onClick={() => handleStyleChange(key)}
                                            className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                                selectedStyle === key ? 'bg-blue-50 border-blue-200' : ''
                                            }`}
                                        >
                                            <div className="font-medium text-gray-900">
                                                {style.name}
                                                {selectedStyle === key && (
                                                    <span className="ml-2 text-blue-600">✓</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {style.description}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Loading overlay */}
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-90">
                        <div className="text-center p-4">
                            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 text-sm sm:text-base">
                                {!mapLoaded ? 'Loading map...' : 'Loading events...'}
                            </p>
                        </div>
                    </div>
                )}

                {/* Error overlay */}
                {error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-50 bg-opacity-95 p-4">
                        <div className="bg-white border border-red-200 rounded-lg p-4 sm:p-6 max-w-xs sm:max-w-md w-full">
                            <div className="flex items-start">
                                <div className="text-red-600 mr-2 sm:mr-3">⚠️</div>
                                <div className="flex-1">
                                    <h3 className="text-red-800 font-semibold text-sm sm:text-base">MapBox Error</h3>
                                    <p className="text-red-600 mt-1 text-xs sm:text-sm">{error}</p>

                                    <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-red-100 rounded text-xs sm:text-sm">
                                        <p className="font-semibold text-red-800">Troubleshooting:</p>
                                        <ul className="mt-2 space-y-1 text-red-700">
                                            <li>• Set MapBox access token</li>
                                            <li>• Install: npm install mapbox-gl</li>
                                            <li>• Check browser console</li>
                                        </ul>
                                    </div>

                                    <button
                                        onClick={() => {
                                            setError(null);
                                            setLoading(true);
                                            setMapLoaded(false);
                                            // Retry initialization
                                            setTimeout(() => window.location.reload(), 100);
                                        }}
                                        className="mt-3 px-3 sm:px-4 py-1 sm:py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-xs sm:text-sm w-full sm:w-auto"
                                    >
                                        Retry
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Fixed bottom instruction */}
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg border">
                <p className="text-xs sm:text-sm text-gray-500 text-center">
                    💡 Click on markers to see event details
                </p>
            </div>
        </div>
    );
};

export default OTGEventsMap;